import {
  parseLanguageSetting,
  readBrowserCopy,
  resolveInterfaceLanguage,
  writeBrowserCopy,
  type InterfaceLanguage,
  type LanguageChoice
} from './languages';

/**
 * Tracker #119 — the Portal's interface-language state, kept out of React so
 * its ordering can be tested (`LanguageContext` is a thin wrapper). The
 * choice is held on the platform and shared with SPFx and the workspace; the
 * browser copy paints the first frame and is replaced by the platform's
 * value when it arrives — unless the user chose while that read was in
 * flight: then the read may set the Microsoft 365 language but never the
 * choice or its copy (Codex review, PR #362).
 */
export interface PortalLanguageState {
  language: InterfaceLanguage;
  /** The saved choice; null when the user never chose (Microsoft 365 → browser → English applies). */
  choice: LanguageChoice | null;
  /** True when the last choice could not be saved (it applies in this browser for this visit). */
  saveFailed: boolean;
}

export interface PortalLanguageDeps {
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  browserLanguages: ReadonlyArray<string>;
  /** GET the signed-in user's setting; null when unreachable. */
  fetchSetting: () => Promise<unknown | null>;
  /** POST an explicit choice; false when it could not be saved. */
  saveSetting: (choice: LanguageChoice) => Promise<boolean>;
  onChange: (state: PortalLanguageState) => void;
}

export class PortalLanguage {
  private choiceValue: LanguageChoice | null;
  private microsoft365Language: string | null = null;
  private saveFailed = false;
  private choiceGeneration = 0;
  private disposed = false;

  public constructor(private readonly deps: PortalLanguageDeps) {
    this.choiceValue = readBrowserCopy(deps.storage);
  }

  public get state(): PortalLanguageState {
    return {
      language: resolveInterfaceLanguage({
        choice: this.choiceValue,
        microsoft365Language: this.microsoft365Language,
        browserLanguages: this.deps.browserLanguages
      }),
      choice: this.choiceValue,
      saveFailed: this.saveFailed
    };
  }

  /** Reads the platform's value once (at start-up). */
  public async load(): Promise<void> {
    const startedAt = this.choiceGeneration;
    let body: unknown | null = null;
    try {
      body = await this.deps.fetchSetting();
    } catch {
      body = null;
    }
    if (this.disposed || body === null) return;
    const setting = parseLanguageSetting(body);
    this.microsoft365Language = setting.microsoft365Language;
    if (this.choiceGeneration === startedAt) {
      this.choiceValue = setting.choice;
      writeBrowserCopy(this.deps.storage, setting.choice);
    }
    this.notify();
  }

  /** An explicit choice from the selector: applies at once, then is saved. */
  public async choose(next: LanguageChoice): Promise<void> {
    this.choiceGeneration += 1;
    const generation = this.choiceGeneration;
    this.choiceValue = next;
    this.saveFailed = false;
    writeBrowserCopy(this.deps.storage, next);
    this.notify();
    let saved = false;
    try {
      saved = await this.deps.saveSetting(next);
    } catch {
      saved = false;
    }
    // Only the latest choice reports its save result.
    if (this.disposed || generation !== this.choiceGeneration) return;
    this.saveFailed = !saved;
    this.notify();
  }

  /** The provider unmounted: late results change nothing. */
  public dispose(): void {
    this.disposed = true;
  }

  private notify(): void {
    if (!this.disposed) this.deps.onChange(this.state);
  }
}
